import viacep from "@/api/viacep";
import Swal from "sweetalert2";

export function formatCurrency(value) {
    // Retorna string vazia para valores null, undefined ou string vazia
    if (value === null || value === undefined || value === '') return "";
    
    let numeric;

    if (typeof value === "string") {
        // Se tiver vírgula, tratamos como número brasileiro
        if (value.includes(',')) {
            numeric = parseFloat(value.replace(/\./g, '').replace(',', '.'));
        } else {
            // Se for número em ponto (ex: "2.11"), só parseFloat direto
            numeric = parseFloat(value);
        }
    } else {
        numeric = value;
    }


    if (isNaN(numeric)) return "";

    const formatted = numeric.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
    });

    return formatted;
}

export function parseCurrency(value) {
    if (!value || value == 'NaN') return '0,00';
    
    if (typeof value !== 'string') value = String(value);

    const cleaned = value.replace(/\s/g, '').replace('R$', '');
    let numeric;
    
    if (cleaned.includes(',')) {
        numeric = parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    } else {
        numeric = parseFloat(cleaned);
    }
    
    // Se não for um número válido, retorna 0,00
    if (isNaN(numeric) || !isFinite(numeric)) {
        return '0,00';
    }
    
    return numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

export function parseCurrencyToNumber(value) {
  if (value === null || value === undefined) return null;

  let s = String(value).trim();
  if (!s) return null;

  // remove "R$" e espaços
  s = s.replace(/\s/g, '').replace(/^R\$\s?/, '');

  // mantém apenas dígitos, vírgula, ponto e sinal
  s = s.replace(/[^\d,.-]/g, '');
  if (!s) return null;

  const lastComma = s.lastIndexOf(',');
  const lastDot   = s.lastIndexOf('.');

  // Se há vírgula e ponto, considera o ÚLTIMO como decimal.
  // Remove o outro como milhar.
  if (lastComma !== -1 && lastDot !== -1) {
    if (lastComma > lastDot) {
      // vírgula é decimal → remove todos os pontos, troca vírgula por ponto
      s = s.replace(/\./g, '').replace(',', '.');
    } else {
      // ponto é decimal → remove todas as vírgulas
      s = s.replace(/,/g, '');
    }
  } else if (lastComma !== -1) {
    // só vírgula → decimal BR
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    // só ponto ou nenhum
    // se só pontos e mais de um, o último é decimal e os anteriores são milhares (Number já lida na maioria dos casos)
    // se não houver separador, segue direto
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function handleInput(value) {
    if (!value) return "0,00";

    // Remove tudo que não for número
    const digitsOnly = value.replace(/\D/g, '');

    // Converte para centavos e formata com vírgula
    const numericValue = (parseFloat(digitsOnly) / 100).toFixed(2);

    // Retorna no formato brasileiro esperado pelo formatCurrency
    return numericValue.replace('.', ','); // ← Isso é o essencial!
}

export function formatDecimal(value) {
    const cleanedValue = value.replace(/[^\d,]/g, "").replace(",", ".");
    const decimalValue = parseFloat(cleanedValue);

    if (isNaN(decimalValue)) {
        return "";
    }
    return decimalValue
        .toFixed(2)
        .replace(".", ",")
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
}

export function parseDecimal(value) {
    if (!value) return "";
    const number = value
        .replace(/\./g, "")
        .replace(",", ".");
    const parsedValue = parseFloat(number);
    return isNaN(parsedValue) ? "" : parsedValue.toFixed(2);
}

export async function validarCampoCep(
    obj,
    { tipoEndereco = null, somenteValidar = false } = {}
) {
    const cepField = obterNomeCampo("Cep", tipoEndereco);
    if (!somenteValidar) {
        const logradouroField = obterNomeCampo("Logradouro", tipoEndereco);
        const bairroField = obterNomeCampo("Bairro", tipoEndereco);
        const cidadeField = obterNomeCampo("Cidade", tipoEndereco);
        const ufField = obterNomeCampo("Uf", tipoEndereco);
        const paisField = obterNomeCampo("Pais", tipoEndereco);

        if (!obj[cepField]) return false;
        obj[cepField] = obj[cepField].replace(/\D/g, "");
        if (obj[cepField].length !== 8) {
            mostarErroCep();
            limparCamposEndereco(obj, tipoEndereco);
            return false;
        }

        try {
            const { data } = await viacep.get(`/ws/${obj[cepField]}/json/`);
            if (data.erro) {
                mostarErroCep();
                limparCamposEndereco(obj, tipoEndereco);
                return false;
            } else {
                obj[logradouroField] = data.logradouro;
                obj[bairroField] = data.bairro;
                obj[cidadeField] = data.localidade;
                obj[ufField] = data.uf;
                obj[paisField] = "BRA";
                return true;
            }
        } catch (error) {
            mostarErroCep();
            limparCamposEndereco(obj, tipoEndereco);
            return false;
        }
    } else {
        try {
            const { data } = await viacep.get(`/ws/${obj[cepField]}/json/`);
            if (data.erro) {
                return false;
            } else {
                return true;
            }
        } catch (error) {
            return false;
        }
    }
}

function obterNomeCampo(base, tipoEndereco) {
    if (!tipoEndereco) return base;
    const tipoEnderecoLower = tipoEndereco.toLowerCase();
    if (tipoEnderecoLower === "outro") {
        return `${base}${capitalize(tipoEndereco)}`;
    } else if (tipoEnderecoLower === "enderecocomercial") {
        switch (base) {
            case "Logradouro":
                return "EnderecoComercial";
            case "Uf":
                return `${capitalize(tipoEndereco)}Estado`;
            default:
                return `${capitalize(tipoEndereco)}${base}`;
        }
    } else {
        return base;
    }
}

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function limparCamposEndereco(obj, tipoEndereco = null) {
    const logradouroField = obterNomeCampo("Logradouro", tipoEndereco);
    const bairroField = obterNomeCampo("Bairro", tipoEndereco);
    const cidadeField = obterNomeCampo("Cidade", tipoEndereco);
    const ufField = obterNomeCampo("Uf", tipoEndereco);
    const paisField = obterNomeCampo("Pais", tipoEndereco);

    obj[logradouroField] = "";
    obj[bairroField] = "";
    obj[cidadeField] = "";
    obj[ufField] = "";
    obj[paisField] = "";
}

function mostarErroCep() {
    Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `O CEP informado não foi encontrado. Verifique e tente novamente ou preencha os campos manualmente. Para CEP's fora do Brasil, o preenchimento manual é necessário.`,
    });
}

export const ufs = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
    'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
    'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];
